import { LoadingStageModel, ValueModel } from '@kts-front/models';
import { Nullable } from '@kts-front/types';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { LeadEntity, LeadEntityApi, leadSchema } from 'entities/Lead';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { PopupStore } from 'stores/local/shared/PopupStore';
import { logZodError } from 'utils/logZodError';

import { NotificationsStore } from '../NotificationsStore';

type Params = {
  notificationStore: NotificationsStore;
};

export class LeadDrawerStore extends PopupStore<number> {
  private readonly _notificationStore: NotificationsStore;
  readonly lead = new ValueModel<Nullable<LeadEntity>>(null);

  readonly loadingStage = new LoadingStageModel();

  readonly showButton = new ValueModel<boolean>(false);

  constructor(params: Params) {
    super();

    this._notificationStore = params.notificationStore;

    makeObservable(this, {
      lead: observable.ref,
      fetch: action.bound,
      openWithShowButton: action.bound,
    });
  }

  openWithShowButton({ id, showButton = false }: { id: number; showButton?: boolean }): void {
    super.open(id);
    this.fetch(id);
    this.showButton.change(showButton);
  }

  open(id: number): void {
    super.open(id);
    this.fetch(id);
  }

  close(): void {
    super.close();
    this.lead.reset();
    this.loadingStage.reset();
  }

  async fetch(id: number): Promise<void> {
    if (!this.entity) {
      return;
    }

    this.loadingStage.loading();

    const request = apiStore.createRequest({
      url: ENDPOINTS.lead.get(id),
    });

    const response = await request.call<LeadEntityApi>();

    if (response.isError) {
      this.loadingStage.error();

      this._notificationStore.showErrorByResponse(response);

      logApiErrWithSentry({
        endpoint: ENDPOINTS.lead.get(id),
        method: 'GET',
        requestPayload: { lead_id: id },
        response,
      });

      return;
    }

    const parsed = leadSchema.safeParse(response.data);

    if (parsed.error) {
      this.loadingStage.error();

      this._notificationStore.showError({ message: parsed.error.message });

      logZodError(parsed.error);

      return;
    }

    runInAction(() => {
      this.lead.change(parsed.data);
      this.loadingStage.success();
    });
  }
}
